import { WEI, } from "@bracket/blocksmith";
import { getTokenDefinition, } from "@bracket/chainography";
import type { TokenIdentifier, } from "@bracket/types";
import { formatDollars, formatWEI, } from "@formatters";
import { useSharedDraftable, } from "@bracket/platform/hooks";
import { useVaults, } from "@bracket/platform/store";
import { assetToUsd, } from "@utils";
import { wagmiConfig as config, } from "@wagmiconfig";
import { useEffect, useState, } from "react";
import styled from "styled-components";
import { getAccount, } from "wagmi/actions";
import { getTheme, themed, FlexFlow, FlexStream, } from "@bracket/ui";



export const FeesRollupCard = () => {
	themed();

	const {
    activeAdminVault: vault,
		ready,
  } = useVaults();

  if(!ready || !vault) {
    return null;
  }


	const {
    symbol,
    nav,
    denomination,
    denomination: {
      address: denoAddress,
      symbol: denoSymbol,
			label: denoLabel,
    },
    mgmt: {
      pendingDeposits,
      totalWithdrawals,
      managerBalances,
    }
  } = vault;

  const [balance, setBalance] = useState<WEI>(WEI.fromSync("0", denomination));

  const { address, } = getAccount(config);
  if(!address) {
    throw new Error("manager wallet could not be resolved");
  }


  useEffect(() => {
    // getBalance(config, { address, token: denoAddress as `0x${string}`, }).then(({ value, }) => setBalance(value));
    const b = managerBalances.find(curr => curr.symbol === denoSymbol);
    if(b) {
      setBalance(WEI.fromSync(b.value, denomination));
    }
  }, [ address, denoAddress, ]);

  const {
		draft,
	} = useSharedDraftable<typeof nav>(nav, { instanceId: symbol, emptyValues: true });

	let { onav, manager_tvl_fee, bracket_tvl_fee, manager_performance_fee, } = draft;
	onav = onav.length ? onav : nav.onav;
	manager_tvl_fee = manager_tvl_fee.length ? manager_tvl_fee : nav.manager_tvl_fee;
	bracket_tvl_fee = bracket_tvl_fee.length ? bracket_tvl_fee : nav.bracket_tvl_fee;
	manager_performance_fee = manager_performance_fee.length ? manager_performance_fee : nav.manager_performance_fee;

	const wONav = WEI.fromSync(onav, denomination);
	const wPendingDeposits = WEI.fromSync(pendingDeposits, denomination);
	const wTotalWithdrawalsShares = WEI.fromSync(totalWithdrawals, denomination);
	const wTotalWithdrawals = wTotalWithdrawalsShares.multiply(wONav);
	const managerTvlFee = WEI.fromSync(manager_tvl_fee || manager_tvl_fee, denomination);
	const managerPerformanceFee = WEI.fromSync(manager_performance_fee, denomination);
  const brktTvlFee = WEI.fromSync(bracket_tvl_fee || nav.bracket_tvl_fee);

	const net = wPendingDeposits.subtract(wTotalWithdrawals);
	const fees = managerTvlFee.add(managerPerformanceFee).add(brktTvlFee).multiply(wONav);
	const diff = wPendingDeposits.add(balance).subtract(wTotalWithdrawals).subtract(fees);
  const due = diff.greaterThan(0n) ? WEI.fromSync("0") : diff;


	return (
			<SummaryTable
				keys={[{ label: "Summary", }, { label: denoLabel, }, { label: "USD", display: denoSymbol === "USDC", }, ]}
				token={denomination}
				values={[
					{ label: "Net", value: net.toString(), },
					{ label: "Fees", value: fees.toString(), },
					{ label: "Wallet Balance", value: balance.toString(), },
					{ label: "Due", value: due.toString(), colorize: true, },
				]}
				widths={["50%", "25%", "25%"]}
			/>
	);

}


const SummaryTable = ({
	keys,
	values,
	token,
	widths,
}: {
	keys: { label: string; width?: string; display?: boolean; }[];
	values: { label: string; value: string; width?: string; display?: boolean; colorize?: boolean; }[];
	token: TokenIdentifier;
	widths?: string[];
}) => {
	const {symbol} = getTokenDefinition(token);
	return (
		<SummaryTableBrace>
			<STHeader>
				{keys.map(({ label, width, display, }, i) => {
					if(label === "USD") {
						if(symbol === "USDC") {
							return null;
						}
					}
					return <HCell key={label} $align={ i > 0 ? "flex-end" : undefined} $width={widths?.[i]}>{label}</HCell>;
				}
				)}
			</STHeader>
			<STBody>
			{
				values.map(({ label, value, width, display, colorize, }) => {
					return (
						<KVTRow key={label} label={label} value={value} token={token} showUsd={symbol !== "USDC"} widths={widths} colorize={label === "Due"}/>
					)
				})
			}
			</STBody>
		</SummaryTableBrace>
	)
}


const KVTRow = ({
  label,
  value,
  token,
	colorize = false,
	showUsd = true,
	widths,
}: { label: string; value: string; token: TokenIdentifier; colorize?: boolean; showUsd?: boolean; widths?: string[]; }) => {

  const [usd, setUsd] = useState(0);
  const tok = getTokenDefinition(token);
  const { decimals, symbol, } = tok;

	useEffect(() => { assetToUsd(value, tok).then(usd => setUsd(usd)); }, [value, tok]);

	return (
		<STRow>
			<STCell $width={widths?.[0]}>{label}</STCell>
			<STCell $align="flex-end" $width={widths?.[1]} color={colorize && Number(value) < 0 ? getTheme().error : undefined}>{formatWEI(value, decimals, symbol === "USDC" ? 2 : 5, true)}</STCell>
			{ showUsd && ( <STCell $align="flex-end" $width={widths?.[2]} color={colorize && Number(value) < 0 ? getTheme().error : undefined}>{formatDollars(usd)}</STCell> ) }
		</STRow>
	);

}


const SummaryTableBrace = styled(FlexFlow)`
	color: ${() => getTheme().surface_on};
	position: relative;
	padding: 1rem;
	border-radius: 1rem;
	background-color: ${() => getTheme().surface_container};
	margin-top: 2rem;
	margin-bottom: 2rem;
`;


const STHeader = styled(FlexStream)`
	position: relative;
	margin-bottom: .25rem;
	border-bottom: 1px solid ${() => getTheme().outline};
`;


const HCell = styled(FlexFlow)<{$align?: string; $width?: string}>`
	align-items: ${({$align}) => $align};
	width: ${({$width}) => $width};
	min-width: ${({$width}) => $width};
	max-width: ${({$width}) => $width};
	padding: .75rem;
`;


const STBody = styled(FlexFlow)`
	position: relative;
	width: 100%;
	max-width: 100%;
	min-width: 100%;
`;


const STRow = styled(FlexStream)`
	position: relative;
	width: 100%;
	max-width: 100%;
	min-width: 100%;
	padding: .25rem .75rem;
`;


const STCell = styled(FlexFlow)<{$align?: string; $width?: string; $color?: string; }>`
	align-items: ${({$align}) => $align};
	width: ${({$width}) => $width};
	min-width: ${({$width}) => $width};
	max-width: ${({$width}) => $width};	
	position: relative;
	${({$color}) => $color && `color: ${$color};`};
`;	
